import _ from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import className from "../../shared/className";
import Icon from "../../shared/Icon";
import Loader from "../../shared/Loader";
import { formatDateTime } from "../../shared/dateTimeUtils";
import ECGStats from "./ECGStats";
import "./StatementEditor.scss";
import StatementEditorStore from "./StatementEditorStore";

class StatementEditor extends React.Component {
  constructor(props) {
    super(props);
    this.store = new StatementEditorStore();
    this.store.statements = this.props.ecg.statements.map((s) =>
      observable({
        id: guid(),
        dirty: false,
        words: s.map((w) => new Word(w)),
        suggestions: null,
        inputValues: Array(s.length + 1).fill(""),
      }),
    );
    this.save = this.save.bind(this);
    this.addStatement = this.addStatement.bind(this);
    this.statementLibrary =
      this.props.statementLibrary?.map((s) => new Word(s)) || [];
  }

  componentDidMount() {
    setTimeout(() => {
      this.refAppendButton?.scrollIntoView({ behavior: "smooth" });
    }, 1000);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    const { cancel, ecg } = this.props;
    return (
      <div className="statement-editor" data-testid="statement-editor">
        <div className="content" ref={(r) => (this.refContent = r)}>
          <div className="acquisition-time">
            {formatDateTime(ecg.ecgDetails.acquisitionDateTime, "-")}
          </div>
          <ECGStats ecg={ecg} />
          <button
            className="btn append-statement"
            onClick={this.addStatement}
            ref={(r) => (this.refAppendButton = r)}
          >
            Append Statement
          </button>
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={getListStyle(snapshot.isDraggingOver)}
                >
                  <this.Paragraph statements={this.store.statements} />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        {this.store.saving ? (
          <Loader />
        ) : (
          <div className="actions">
            <button className="cancel btn secondary" onClick={cancel}>
              {" "}
              Cancel{" "}
            </button>
            <button className="cancel btn primary" onClick={this.save}>
              {" "}
              Confirm{" "}
            </button>
          </div>
        )}
      </div>
    );
  }

  Paragraph = observer(({ statements }) =>
    statements.filter(_.identity).map((sentence, index) => (
      <Draggable key={sentence.id} draggableId={sentence.id} index={index}>
        {(provided, snapshot) => (
          <div
            className="sentence-container"
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(
              snapshot.isDragging,
              provided.draggableProps.style,
            )}
          >
            <Sentence sentence={sentence} statementEditor={this} />
          </div>
        )}
      </Draggable>
    )),
  );

  onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    this.store.statements = reorder(
      this.store.statements,
      result.source.index,
      result.destination.index,
    );
  };

  save() {
    if (this.store.saving) return;
    this.store.saving = true;
    this.props
      .save(
        this.store.statements.map((s) =>
          s.words.map((w) => ({
            statementCode: w.code,
            statementText: w.text,
            ...w,
            id: undefined,
          })),
        ),
      )
      .finally(() => (this.store.saving = false));
  }

  addStatement() {
    const id = guid();
    this.store.statements.push(
      observable({
        id,
        words: [],
        suggestions: null,
        dirty: false,
        inputValues: [""],
      }),
    );
    setTimeout(() => {
      document.getElementById(id).scrollIntoView();
    }, 100);
  }

  updateSuggestions({ e, sentence }) {
    this.normalizeEvent(e);
    if (this.specialKeys.includes(e.key)) {
      return e.preventDefault();
    }
    const freeText = e.target.value.trim();
    if (freeText && freeText.length >= 2) {
      sentence.suggestions = [
        {
          text: freeText,
          selected: true,
        },
        ..._(this.statementLibrary)
          .filter(
            (s) =>
              s.code.indexOf(freeText.toUpperCase()) !== -1 ||
              s.text.toUpperCase().indexOf(freeText.toUpperCase()) !== -1,
          )
          .sortBy((s) => {
            const searchText = freeText.toUpperCase();
            if (s.code === searchText) return 0;
            if (s.code.indexOf(searchText) === 0) return 1;
            if (s.text.toUpperCase() === searchText) return 2;
            if (s.code.indexOf(searchText) !== -1) return 3;
            if (s.text.toUpperCase().indexOf(searchText) === 0) return 4;
            return 5;
          })
          .map(_.clone)
          .value(),
      ];
    } else {
      sentence.suggestions = null;
    }
  }

  insertSuggestion({ e, suggestion, sentence }) {
    suggestion.selected = undefined;
    const sentenceNode = e.target
      .closest(".sentence-container")
      .querySelector(".sentence");
    sentenceNode.blur();
    const index = _.indexOf(
      sentenceNode.querySelectorAll("input"),
      sentenceNode.querySelector("input:focus"),
    );
    sentence.words.splice(
      index,
      0,
      new Word({
        statementText: suggestion.text,
        statementCode: suggestion.code,
        number: suggestion.number,
        id: guid(),
      }),
    );
    sentence.suggestions = null;
    sentence.inputValues = Array(sentence.words.length + 1).fill("");
    setTimeout(
      () => sentenceNode.querySelectorAll("input")[index + 1].focus(),
      100,
    );
  }

  normalizeEvent(e) {
    // Normalize older key names.
    if (e.key)
      e.key = e.key.replace(
        /(^(Up|Down|Left|Right)$)/,
        (match) => `Arrow${match}`,
      );
  }
  specialKeys = ["ArrowUp", "ArrowDown", "Enter", "Tab"];
  statementKeyPress({ e, sentence, index }) {
    this.normalizeEvent(e);
    const keyName = e.key;
    if (keyName === "Backspace" && !e.target.value && index) {
      sentence.words.splice(index - 1, 1);
      e.target.closest(".sentence").querySelectorAll("input")[
        // eslint-disable-next-line no-unexpected-multiline
        index - 1
      ].focus();
    }
    if (sentence.suggestions && this.specialKeys.includes(keyName)) {
      e.preventDefault();
      const selectedSuggestion = sentence.suggestions.find((x) => x.selected);
      const selectedIndex = sentence.suggestions.indexOf(selectedSuggestion);
      if (["Enter", "Tab"].includes(keyName)) {
        this.insertSuggestion({
          e,
          sentence,
          suggestion: selectedSuggestion,
        });
      }
      if (keyName === "ArrowUp") {
        if (selectedIndex > 0) {
          selectedSuggestion.selected = false;
          sentence.suggestions[selectedIndex - 1].selected = true;
        }
      } else if (keyName === "ArrowDown") {
        if (selectedIndex < sentence.suggestions.length - 1) {
          selectedSuggestion.selected = false;
          sentence.suggestions[selectedIndex + 1].selected = true;
        }
      }
      if (sentence.suggestions) {
        sentence.suggestions = [...sentence.suggestions];
      }
    }
  }
}

export default observer(StatementEditor);

function guid() {
  return (
    Math.random().toString(36).substring(2) + performance.now().toString(36)
  );
}

class Word {
  constructor(word = { statementText: "" }) {
    Object.assign(this, word);
    this.id = this.id || guid();
  }
}

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  marginTop: isDragging ? "-40px" : null,
  background: "#fff",
  boxShadow: isDragging ? "0px 8px 6px rgba(0,0,0,0.4)" : "none",
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? "#eee" : "none",
  padding: 8,
});

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function ResizingInput(props) {
  return (
    <span className="resizing-input">
      {props.value || "."}
      <input {...props} />
    </span>
  );
}

const Sentence = observer(({ sentence, statementEditor }) => {
  function getSentenceInputValue(i) {
    return sentence.inputValues[i];
  }
  function setSentenceInputValue(i) {
    return (e) => (sentence.inputValues[i] = e.currentTarget.value);
  }

  function deleteWord(word) {
    _.remove(sentence.words, (w) => w === word);
    sentence.inputValues = Array(sentence.words + 1).fill("");
  }
  const sentenceClick = (e) => {
    if (e.target === e.currentTarget) {
      _.last(e.target.querySelectorAll("input")).focus();
    }
  };

  const Input = (props) => (
    <ResizingInput
      value={getSentenceInputValue(props.index)}
      onChange={setSentenceInputValue(props.index)}
      onKeyDown={(e) =>
        statementEditor.statementKeyPress({ e, sentence, index: props.index })
      }
      onKeyUp={(e) => statementEditor.updateSuggestions({ e, sentence })}
      onBlur={() => {
        if (sentence.inputValues[props.index])
          sentence.inputValues[props.index] = "";
        sentence.suggestion = null;
      }}
    />
  );

  return (
    <React.Fragment>
      <button
        className="delete-sentence"
        onClick={() => {
          _.remove(statementEditor.store.statements, (s) => s === sentence);
        }}
      >
        <Icon symbol="x" />
      </button>
      <Icon symbol="menu" />
      <div className="sentence" id={sentence.id} onClick={sentenceClick}>
        {Input({ index: 0 })}
        {sentence.words.map((word, i) => (
          <React.Fragment key={word.id}>
            <div className="word" id={word.id}>
              {word.statementText}
              <button className="delete-word" onClick={() => deleteWord(word)}>
                <Icon symbol="x" />
              </button>
            </div>
            {Input({ index: i + 1 })}
          </React.Fragment>
        ))}
      </div>
      {sentence.suggestions ? (
        <div className="suggestions">
          {sentence.suggestions.map((suggestion) => (
            <div
              key={suggestion.code + suggestion.number}
              {...className({
                suggestion: 1,
                selected: suggestion.selected,
              })}
              onClick={(e) =>
                statementEditor.insertSuggestion({ e, suggestion, sentence })
              }
            >
              {suggestion.code ? (
                <span className="code">{suggestion.code} :</span>
              ) : null}
              {suggestion.text}
            </div>
          ))}
        </div>
      ) : null}
    </React.Fragment>
  );
});
